import { timestampDate } from "@bufbuild/protobuf/wkt";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { BookmarkIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTimezone } from "@/contexts/TimezoneContext";
import i18n from "@/i18n";
import { cn } from "@/lib/utils";
// ❌ REMOVED: Visibility import (v0.66.0 - all memos are private)
import type { User } from "@/types/proto/api/v1/user_service_pb";
import { useTranslate } from "@/utils/i18n";
// ❌ REMOVED: convertVisibilityToString import (v0.66.0 - all memos are private)
import JournalActionMenu from "../../JournalActionMenu";
import { ReactionSelector } from "../../JournalReactionListView";
import UserAvatar from "../../UserAvatar";
// ❌ REMOVED: VisibilityIcon import (v0.66.0 - all memos are private)
import { useJournalViewContext, useJournalViewDerived } from "../JournalViewContext";
import type { MemoHeaderProps } from "../types";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const JournalHeader: React.FC<MemoHeaderProps> = ({
  showCreator,
  // ❌ REMOVED: showVisibility prop (v0.66.0 - all memos are private)
  showPinned,
  onEdit,
  onGotoDetail,
  onUnpin,
  onToggleNsfwVisibility,
  onShowVersionHistory,
}) => {
  const t = useTranslate();
  const [reactionSelectorOpen, setReactionSelectorOpen] = useState(false);
  const { timezone: userTimezone } = useTimezone(); // Get user's preferred timezone

  const { memo, creator, currentUser, isArchived, readonly, showNSFWContent, nsfw } = useJournalViewContext();
  const { relativeTimeFormat } = useJournalViewDerived();

  // Format display time using user's preferred timezone
  const displayTime = isArchived ? (
    memo.displayTime ? (
      dayjs(timestampDate(memo.displayTime)).tz(userTimezone).locale(i18n.language).format("lll")
    ) : undefined
  ) : (
    <relative-time
      datetime={memo.displayTime ? timestampDate(memo.displayTime)?.toISOString() : undefined}
      lang={i18n.language}
      format={relativeTimeFormat}
    ></relative-time>
  );

  return (
    <div className="w-full flex flex-row justify-between items-center gap-2">
      <div className="w-auto max-w-[calc(100%-8rem)] grow flex flex-row justify-start items-center">
        {showCreator && creator ? (
          <CreatorDisplay creator={creator} displayTime={displayTime} onGotoDetail={onGotoDetail} />
        ) : (
          <TimeDisplay displayTime={displayTime} onGotoDetail={onGotoDetail} />
        )}
      </div>

      <div className="flex flex-row justify-end items-center select-none shrink-0 gap-2">
        {currentUser && !isArchived && (
          <ReactionSelector
            className={cn("border-none w-auto h-auto", reactionSelectorOpen && "block!", "block sm:hidden sm:group-hover:block")}
            memo={memo}
            onOpenChange={setReactionSelectorOpen}
          />
        )}

        {/* ❌ REMOVED: Visibility icon display (v0.66.0 - all memos are private) */}

        {showPinned && memo.pinned && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="cursor-pointer">
                  <BookmarkIcon className="w-4 h-auto text-primary" onClick={onUnpin} />
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t("common.unpin")}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}

        {nsfw && showNSFWContent && onToggleNsfwVisibility && (
          <span className="cursor-pointer">
            <EyeOffIcon className="w-4 h-auto text-primary" onClick={onToggleNsfwVisibility} />
          </span>
        )}

        <JournalActionMenu memo={memo} readonly={readonly} onEdit={onEdit} onShowVersionHistory={onShowVersionHistory} />
      </div>
    </div>
  );
};

interface CreatorDisplayProps {
  creator: User;
  displayTime: React.ReactNode;
  onGotoDetail: () => void;
}

const CreatorDisplay: React.FC<CreatorDisplayProps> = ({ creator, displayTime, onGotoDetail }) => (
  <div className="w-full flex flex-row justify-start items-center">
    <Link className="w-auto hover:opacity-80 rounded-md transition-colors" to={`/u/${encodeURIComponent(creator.username)}`} viewTransition>
      <UserAvatar className="mr-2 shrink-0" avatarUrl={creator.avatarUrl} />
    </Link>
    <div className="w-full flex flex-col justify-center items-start">
      <Link
        className="block leading-tight hover:opacity-80 rounded-md transition-colors truncate text-muted-foreground"
        to={`/u/${encodeURIComponent(creator.username)}`}
        viewTransition
      >
        {creator.displayName || creator.username}
      </Link>
      <button
        type="button"
        className="w-auto -mt-0.5 text-xs leading-tight text-muted-foreground select-none cursor-pointer hover:opacity-80 transition-colors text-left"
        onClick={onGotoDetail}
      >
        {displayTime}
      </button>
    </div>
  </div>
);

interface TimeDisplayProps {
  displayTime: React.ReactNode;
  onGotoDetail: () => void;
}

const TimeDisplay: React.FC<TimeDisplayProps> = ({ displayTime, onGotoDetail }) => (
  <button
    type="button"
    className="w-full text-sm leading-tight text-muted-foreground select-none cursor-pointer hover:text-foreground transition-colors text-left"
    onClick={onGotoDetail}
  >
    {displayTime}
  </button>
);

export default JournalHeader;
